import toast from 'react-hot-toast';
import { PublicClientApplication, InteractionRequiredAuthError } from '@azure/msal-browser';
import { msalConfig } from '../config/authConfig';

// Create MSAL instance for authentication
let msalInstance: PublicClientApplication | null = null;

async function getMsalInstance(): Promise<PublicClientApplication | null> {
  if (!msalInstance) {
    try {
      msalInstance = new PublicClientApplication(msalConfig);
      await msalInstance.initialize();
    } catch (error) {
      console.error('Failed to initialize MSAL for API:', error);
      return null;
    }
  }
  return msalInstance;
}

async function getAuthToken(): Promise<string | null> {
  try {
    const msal = await getMsalInstance();
    if (!msal) return null;

    const accounts = msal.getAllAccounts();
    if (accounts.length === 0) {
      console.warn('No MSAL accounts found for API auth');
      return null;
    }

    const tokenRequest = {
      scopes: ['openid', 'profile', 'email', 'User.Read'],
      account: accounts[0]
    };

    try {
      const tokenResponse = await msal.acquireTokenSilent(tokenRequest);
      // Use ID token for backend API calls - backend validates token audience as client_id
      return tokenResponse.idToken || tokenResponse.accessToken;
    } catch (silentError) {
      const errorCode = (silentError as { errorCode?: string })?.errorCode;
      const requiresInteraction =
        silentError instanceof InteractionRequiredAuthError ||
        errorCode === 'monitor_window_timeout' ||
        errorCode === 'block_token_requests' ||
        errorCode === 'token_renewal_error';

      if (requiresInteraction) {
        try {
          const popupResponse = await msal.acquireTokenPopup(tokenRequest);
          return popupResponse.idToken || popupResponse.accessToken;
        } catch (popupError) {
          console.error('Popup token acquisition failed:', popupError);
        }
      } else {
        console.warn('Silent token acquisition failed with non-interaction error:', silentError);
      }
    }
  } catch (error) {
    console.error('Failed to get auth token for API:', error);
  }

  return null;
}

// Cold start configuration for Azure Functions
const COLD_START_CONFIG = {
  // Azure Functions can take 30-120 seconds to cold start
  TIMEOUT_MS: 120000, // 2 minutes
  RETRY_ATTEMPTS: 3,
  RETRY_DELAY_MS: 2000,
  WARMING_TIMEOUT_MS: 180000, // 3 minutes for initial warming
};

interface ApiRequestOptions {
  timeout?: number;
  retries?: number;
  showToast?: boolean;
  correlationId?: string;
  connectionId?: string;
  isWarming?: boolean;
  signal?: AbortSignal;
}

interface ApiResponse<T = unknown> {
  data?: T;
  error?: string;
  status: number;
  isColdStart?: boolean;
}

class ColdStartAwareApi {
  private baseUrl: string;
  private lastSuccessfulCall: Date | null = null;

  constructor() {
    this.baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:7071/api';
  }

  /**
   * Check if the function is likely in cold start state
   */
  private isLikelyColdStart(): boolean {
    if (!this.lastSuccessfulCall) return true;
    
    // If last successful call was more than 20 minutes ago, assume cold start
    const timeSinceLastCall = Date.now() - this.lastSuccessfulCall.getTime();
    return timeSinceLastCall > 20 * 60 * 1000; // 20 minutes
  }

  /**
   * Enhanced fetch with cold start handling
   */
  private async fetchWithColdStartHandling<T>(
    endpoint: string,
    options: RequestInit = {},
    apiOptions: ApiRequestOptions = {}
  ): Promise<ApiResponse<T>> {
    const {
      timeout = this.isLikelyColdStart() ? COLD_START_CONFIG.WARMING_TIMEOUT_MS : COLD_START_CONFIG.TIMEOUT_MS,
      retries = COLD_START_CONFIG.RETRY_ATTEMPTS,
      showToast = true,
      correlationId,
      connectionId
    } = apiOptions;

    const url = `${this.baseUrl}${endpoint}`;
    const controller = new AbortController();
    
    // Set timeout
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    // Get auth token
    const authToken = await getAuthToken();

    // Add cold start headers
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Client-Version': '1.0.0',
      'X-Cold-Start-Aware': 'true',
    };

    // Add authorization header if token available
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
    }

    // Merge additional headers if provided
    if (options.headers) {
      Object.assign(headers, options.headers);
    }

    if (correlationId) {
      headers['X-Correlation-Id'] = correlationId;
    }

    if (connectionId) {
      headers['X-Connection-Id'] = connectionId;
    }

    const requestOptions: RequestInit = {
      ...options,
      headers,
      signal: controller.signal,
    };

    let lastError: Error | null = null;

    for (let attempt = 1; attempt <= retries; attempt++) {
      try {
        if (showToast && attempt === 1 && this.isLikelyColdStart()) {
          toast.loading(
            '🥶 Azure Function startet (Cold Start) - Dies kann bis zu 2 Minuten dauern...',
            { id: 'coldstart', duration: timeout }
          );
        }

        const startTime = Date.now();
        const response = await fetch(url, requestOptions);
        const duration = Date.now() - startTime;

        clearTimeout(timeoutId);

        // Log performance
        if (duration > 10000) {
          console.warn(`🐌 Slow API response: ${endpoint} took ${duration}ms`);
        }

        let data: T | undefined;
        let errorMessage: string | undefined;

        try {
          const responseText = await response.text();
          if (responseText) {
            data = JSON.parse(responseText);
          }
        } catch (parseError) {
          console.warn('Failed to parse response as JSON:', parseError);
        }

        // Handle different response codes
        if (response.ok) {
          this.lastSuccessfulCall = new Date();
          
          if (showToast) {
            toast.dismiss('coldstart');
            if (duration > 30000) {
              toast.success('✅ Azure Function erfolgreich gestartet!', { duration: 3000 });
            }
          }

          return {
            data,
            status: response.status,
            isColdStart: duration > 30000
          };
        } else {
          // Handle error responses
          if (data && typeof data === 'object' && 'error' in data) {
            errorMessage = (data as { error: string }).error;
          } else {
            errorMessage = `HTTP ${response.status}: ${response.statusText}`;
          }

          // Don't retry on client errors (4xx)
          if (response.status >= 400 && response.status < 500) {
            if (showToast) {
              toast.dismiss('coldstart');
              toast.error(`Fehler: ${errorMessage}`);
            }
            
            return {
              error: errorMessage,
              status: response.status
            };
          }

          // Retry on server errors (5xx) or network issues
          throw new Error(errorMessage);
        }

      } catch (error) {
        lastError = error as Error;
        clearTimeout(timeoutId);

        // Handle abort errors (timeout)
        if (error instanceof Error && error.name === 'AbortError') {
          console.warn(`⏰ Request timeout on attempt ${attempt}/${retries} for ${endpoint}`);
          
          if (attempt === retries) {
            if (showToast) {
              toast.dismiss('coldstart');
              toast.error(
                '⏰ Timeout: Azure Function antwortet nicht. Bitte versuchen Sie es erneut.',
                { duration: 5000 }
              );
            }
            
            return {
              error: 'Request timeout - Azure Function möglicherweise nicht verfügbar',
              status: 408
            };
          }
        } else {
          console.warn(`🔄 Retry ${attempt}/${retries} for ${endpoint}:`, (error as Error).message);
        }

        // Wait before retry (exponential backoff)
        if (attempt < retries) {
          const delay = COLD_START_CONFIG.RETRY_DELAY_MS * Math.pow(2, attempt - 1);
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    }

    // All attempts failed
    if (showToast) {
      toast.dismiss('coldstart');
      toast.error(
        `❌ Fehler nach ${retries} Versuchen: ${lastError?.message || 'Unbekannter Fehler'}`,
        { duration: 5000 }
      );
    }

    return {
      error: lastError?.message || 'Request failed after all retries',
      status: 500
    };
  }

  /**
   * GET request with cold start handling
   */
  async get<T>(endpoint: string, options: ApiRequestOptions = {}): Promise<ApiResponse<T>> {
    return this.fetchWithColdStartHandling<T>(endpoint, { method: 'GET' }, options);
  }

  /**
   * POST request with cold start handling
   */
  async post<T>(endpoint: string, data?: Record<string, unknown>, options: ApiRequestOptions = {}): Promise<ApiResponse<T>> {
    const requestOptions: RequestInit = {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    };

    return this.fetchWithColdStartHandling<T>(endpoint, requestOptions, options);
  }

  /**
   * PUT request with cold start handling
   */
  async put<T>(endpoint: string, data?: Record<string, unknown>, options: ApiRequestOptions = {}): Promise<ApiResponse<T>> {
    const requestOptions: RequestInit = {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    };

    return this.fetchWithColdStartHandling<T>(endpoint, requestOptions, options);
  }

  /**
   * DELETE request with cold start handling
   */
  async delete<T>(endpoint: string, options: ApiRequestOptions = {}): Promise<ApiResponse<T>> {
    return this.fetchWithColdStartHandling<T>(endpoint, { method: 'DELETE' }, options);
  }

  /**
   * Warm up the Azure Function with a health check
   */
  async warmUp(): Promise<boolean> {
    console.log('🔥 Warming up Azure Function...');
    
    try {
      const response = await this.get('/health', {
        timeout: COLD_START_CONFIG.WARMING_TIMEOUT_MS,
        retries: 1,
        showToast: true,
        isWarming: true
      });

      if (response.data) {
        console.log('✅ Azure Function is warm and ready');
        return true;
      } else {
        console.warn('⚠️ Warming failed:', response.error);
        return false;
      }
    } catch (error) {
      console.error('❌ Warming error:', error);
      return false;
    }
  }

  /**
   * Get the current status of the function (warm/cold)
   */
  getStatus(): 'warm' | 'cold' | 'unknown' {
    if (!this.lastSuccessfulCall) return 'unknown';
    
    const timeSinceLastCall = Date.now() - this.lastSuccessfulCall.getTime();
    if (timeSinceLastCall < 5 * 60 * 1000) return 'warm'; // 5 minutes
    if (timeSinceLastCall < 20 * 60 * 1000) return 'cold'; // 20 minutes
    return 'unknown';
  }

  /**
   * Upload files for analysis
   */
  async uploadFiles(formData: FormData, tenantId: string, connectionId: string): Promise<ApiResponse<{
    success: boolean;
    files: Array<{ fileUrl: string; analysisId: string }>;
    message: string;
    correlationId: string;
  }>> {
    const options: ApiRequestOptions = {
      showToast: true,
      connectionId
    };

    // For FormData, we need to handle it differently
    const requestOptions: RequestInit = {
      method: 'POST',
      body: formData
    };

    // Remove Content-Type header for FormData - browser will set it with boundary
    return this.fetchWithColdStartHandling('/analyze', requestOptions, options);
  }

  /**
   * Analyze uploaded images
   */
  async analyzeUploadedImages(analysisIds: string[], tenantId: string, correlationId: string): Promise<ApiResponse<{
    success: boolean;
    analysisId: string;
    status: string;
    results: Array<{
      lang: string;
      title: string;
      description: string;
      tags: string[];
    }>;
    correlationId: string;
  }>> {
    const options: ApiRequestOptions = {
      showToast: true,
      correlationId
    };

    return this.post('/analyze/results', {
      analysisIds,
      tenantId
    }, options);
  }
}

// Create and export singleton instance
export const api = new ColdStartAwareApi();

// Export utilities
export { COLD_START_CONFIG };
export type { ApiRequestOptions, ApiResponse };